import DashboardIcon from '@mui/icons-material/Dashboard';
import SchoolIcon from '@mui/icons-material/School';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import EventIcon from '@mui/icons-material/Event';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DescriptionIcon from '@mui/icons-material/Description';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PeopleIcon from '@mui/icons-material/People';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CampaignIcon from '@mui/icons-material/Campaign';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SecurityIcon from '@mui/icons-material/Security';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ApartmentIcon from '@mui/icons-material/Apartment';
import TableChartIcon from '@mui/icons-material/TableChart';
import ArticleIcon from '@mui/icons-material/Article';
import SettingsIcon from '@mui/icons-material/Settings';
import type { SvgIconComponent } from '@mui/icons-material';

export interface NavItem {
  label: string;
  path: string;
  icon: SvgIconComponent;
  group?: string;
  /**
   * Permission slug required to see this nav item (e.g. 'admissions.view').
   * Items with no `requiredPermission` are visible to every authenticated user.
   */
  requiredPermission?: string;
}

export const navItems: NavItem[] = [
  // ── Core ──────────────────────────────────────────────────────────────────
  { label: 'Dashboard',         path: '/dashboard',          icon: DashboardIcon,      group: 'Core' },

  // ── Operations ────────────────────────────────────────────────────────────
  { label: 'Admissions',        path: '/admissions',         icon: SchoolIcon,         group: 'Operations',  requiredPermission: 'admissions.view' },
  { label: 'Student Sessions',  path: '/student-sessions',   icon: AssignmentIndIcon,  group: 'Operations',  requiredPermission: 'admissions.view' },
  { label: 'Enquiries',         path: '/enquiries',          icon: ContactMailIcon,    group: 'Operations',  requiredPermission: 'enquiries.view' },
  { label: 'Transportation',    path: '/vehicles',           icon: DirectionsBusIcon,  group: 'Operations',  requiredPermission: 'transport.view' },

  // ── Academic ──────────────────────────────────────────────────────────────
  { label: 'Promotions',        path: '/admissions/promote', icon: SwapHorizIcon,      group: 'Academic',    requiredPermission: 'promotions.view' },
  { label: 'Attendance',        path: '/attendance',         icon: FactCheckIcon,      group: 'Academic',    requiredPermission: 'attendance.view' },
  { label: 'Home Extension',    path: '/homework',           icon: MenuBookIcon,       group: 'Academic',    requiredPermission: 'homework.view' },

  { label: 'Documents',         path: '/documents',          icon: DescriptionIcon,    group: 'Academic',    requiredPermission: 'documents.view' },

  // ── Engagement ────────────────────────────────────────────────────────────
  { label: 'Events',            path: '/events',             icon: EventIcon,          group: 'Engagement',  requiredPermission: 'events.view' },
  { label: 'Gallery',           path: '/gallery',            icon: PhotoLibraryIcon,   group: 'Engagement',  requiredPermission: 'gallery.view' },
  { label: "Limi's Moments",    path: '/moments',            icon: AutoAwesomeIcon,    group: 'Engagement',  requiredPermission: 'moments.view' },

  { label: 'Announcements',     path: '/announcements',      icon: NotificationsIcon,  group: 'Engagement',  requiredPermission: 'notifications.view' },

  // ── Finance ───────────────────────────────────────────────────────────────
  { label: 'Finance Dashboard',    path: '/finance',              icon: AccountBalanceIcon, group: 'Finance', requiredPermission: 'dashboard.finance' },
  { label: 'Invoices',             path: '/invoices',             icon: ReceiptIcon,        group: 'Finance', requiredPermission: 'invoices.view' },
  { label: 'Fee Payment Requests', path: '/fee-payment-requests', icon: UploadFileIcon,     group: 'Finance', requiredPermission: 'invoices.view' },
  { label: 'Fee Structure View',   path: '/fee-structures/view',  icon: TableChartIcon,     group: 'Finance', requiredPermission: 'fee_structures.view' },
  { label: 'Fee Reminders',        path: '/fee-reminders',         icon: CampaignIcon,       group: 'Finance', requiredPermission: 'invoices.view' },

  // ── People ────────────────────────────────────────────────────────────────
  { label: 'Staff Profiles',    path: '/staff-profiles',     icon: PeopleIcon,         group: 'People',      requiredPermission: 'staff.view' },

  // ── Management ────────────────────────────────────────────────────────────
  { label: 'Classes',           path: '/classes',            icon: SchoolIcon,         group: 'Management',  requiredPermission: 'classes.view' },
  { label: 'Branches',          path: '/settings/branches',  icon: ApartmentIcon,      group: 'Management',  requiredPermission: 'branches.view' },
  { label: 'School Schedule',   path: '/settings/schedule',  icon: ScheduleIcon,       group: 'Management',  requiredPermission: 'settings.view' },
  { label: 'General Settings',  path: '/settings/general',   icon: SettingsIcon,       group: 'Management',  requiredPermission: 'settings.view' },
  { label: 'Academic Sessions', path: '/academic-sessions',  icon: CalendarTodayIcon,  group: 'Management',  requiredPermission: 'academic_sessions.view' },
  { label: 'Fee Structures',    path: '/fee-structures',     icon: AttachMoneyIcon,    group: 'Management',  requiredPermission: 'fee_structures.view' },

  // ── System ────────────────────────────────────────────────────────────────
  { label: 'System Users',      path: '/users',              icon: PeopleIcon,         group: 'System',      requiredPermission: 'users.view' },
  { label: 'CMS Pages',         path: '/cms',                icon: ArticleIcon,        group: 'System',      requiredPermission: 'cms.view' },
  { label: 'Role Management',   path: '/roles',              icon: SecurityIcon,       group: 'System',      requiredPermission: 'rbac.view' },
  { label: 'Profile',           path: '/profile',            icon: PersonIcon,         group: 'System' },
];

export const navGroups = ['Core', 'Operations', 'Academic', 'Engagement', 'Finance', 'Transport', 'People', 'Management', 'System'] as const;
